import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { SmartQueryBar } from './SmartQueryBar';
import type { SmartQueryBarProps } from './SmartQueryBar';

type SmartQueryPopoverProps = SmartQueryBarProps;

export function SmartQueryPopover(props: SmartQueryPopoverProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', escHandler, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', escHandler, true);
    };
  }, [open]);

  if (!props.enabled) return null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t('common.ai.ask_ai', 'Ask AI')}
        aria-label={t('common.ai.ask_ai', 'Ask AI')}
        className={`flex h-7 w-7 items-center justify-center rounded border ${
          open || props.query
            ? 'border-primary bg-primary/10 text-primary'
            : 'border-csm-border bg-white text-csm-ink-soft hover:text-primary'
        }`}
      >
        <Sparkles size={14} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-max rounded border border-csm-border bg-white p-3 shadow-lg">
          <SmartQueryBar {...props} />
        </div>
      )}
    </div>
  );
}
